import { typography, radius, shadow } from '@/shared/theme/tokens';
import { BCAST } from './broadcastMeta';

/**
 * The persistent on-air "bug" — the corner badge every broadcast channel keeps
 * pinned. A pulsing red LIVE dot, the channel/deployment identity, the running
 * wall clock, and the data-freshness readout. When the feed is stale the
 * freshness chip greys out and reads STALE (never flashing) — the trust signal.
 */
export function LiveBug({ deploymentName, clock, freshness, jitterActive, animate }) {
  const time = clock.toLocaleTimeString(undefined, {
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
  const { stale, ageSeconds } = freshness;
  const freshColor = stale ? BCAST.inkFaint : BCAST.ok;
  const ageLabel = ageSeconds < 2 ? 'NOW' : `${ageSeconds}s AGO`;
  // In a demo build the values are synthetic; say so honestly on the bug.
  const sourceLabel = jitterActive ? 'SIMULATED FEED' : 'LIVE FEED';

  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 0,
      background: BCAST.panel, border: `1px solid ${BCAST.lineBright}`,
      borderRadius: radius.sm, overflow: 'hidden',
      boxShadow: shadow.toast,
    }}>
      {/* LIVE block */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 7, padding: '7px 11px',
        background: BCAST.live,
      }}>
        <span style={{
          width: 9, height: 9, borderRadius: radius.full, background: BCAST.studio,
          animation: animate ? 'bc-bug 1.1s steps(1,end) infinite' : 'none',
        }} />
        <span style={{
          fontFamily: BCAST.heading, fontSize: typography.fontSize.md,
          fontWeight: typography.fontWeight.extrabold, letterSpacing: typography.letterSpacing.widest,
          color: BCAST.studio,
        }}>
          LIVE
        </span>
      </div>

      {/* identity + clock + freshness */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '6px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            fontFamily: BCAST.heading, fontSize: typography.fontSize.sm,
            fontWeight: typography.fontWeight.bold, letterSpacing: typography.letterSpacing.wide,
            textTransform: 'uppercase', color: BCAST.ink,
            maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {deploymentName || 'CONTROL ROOM'}
          </span>
          <span style={{
            fontFamily: BCAST.mono, fontSize: typography.fontSize.md, color: BCAST.ink,
            fontVariantNumeric: 'tabular-nums', letterSpacing: typography.letterSpacing.wide,
          }}>
            {time}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{
            width: 6, height: 6, borderRadius: radius.full, background: freshColor, flexShrink: 0,
            boxShadow: stale ? 'none' : `0 0 6px ${freshColor}`,
            animation: animate && !stale ? 'bc-pulse 1.8s steps(1,end) infinite' : 'none',
          }} />
          <span style={{
            fontFamily: BCAST.mono, fontSize: typography.fontSize.xs, color: freshColor,
            letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase',
          }}>
            {stale ? 'DATA STALE' : 'DATA'} · {ageLabel}
          </span>
          <span style={{ color: BCAST.line }}>│</span>
          <span style={{
            fontFamily: BCAST.mono, fontSize: typography.fontSize.xs, color: BCAST.inkFaint,
            letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase',
          }}>
            {sourceLabel}
          </span>
        </div>
      </div>
    </div>
  );
}
